import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  Fade,
  Alert,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { 
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import { useCreateProjectComment, useUpdateProjectComment } from "../../../../hooks/api/projectCommentsApiHooks";
import CustomButton from "components/shared/button/CustomButton";
import { uploadFileToOneDrive } from "../../../../services/api/api";
import FileManager from "../../../fileManager/FileManager";
import { getUsers } from "../../../../services/api/admin/adminUsersApi";

/**
 * Formulário para criação de novos comentários
 * Seguindo padrões do projeto JBE Engenharia
 */
function CommentForm({ projectId, projectNumber, onCommentAdded, editingComment, onCancelEdit }) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [content, setContent] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [validationError, setValidationError] = useState("");
  const [pendingTags, setPendingTags] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);

  // Mention states
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [allUsers, setAllUsers] = useState(null);
  const textFieldRef = useRef(null);
  const mentionDebounceRef = useRef(null);

  const maxLength = 2000;
  const isEditing = !!editingComment;

  // Hook para criar/editar comentário
  const { createComment, createCommentLoading, createCommentError } = useCreateProjectComment();
  const { updateComment, updateCommentLoading } = useUpdateProjectComment();

  // Quando entra em modo edição, popula o input
  React.useEffect(() => {
    if (editingComment) {
      // Separa texto puro das tags
      const tagRegex = /\[(mencao|arquivo):([^|]+)\|([^\]]+)\]/g;
      const tags = [];
      let match;
      let textOnly = editingComment.content;
      while ((match = tagRegex.exec(editingComment.content)) !== null) {
        tags.push({ type: match[1], label: match[2], id: match[3] });
      }
      textOnly = textOnly.replace(tagRegex, '').trim();
      setContent(textOnly);
      setCharCount(textOnly.length);
      setPendingTags(tags);
      setValidationError("");
      // Foca no input
      setTimeout(() => textFieldRef.current?.focus(), 100);
    }
  }, [editingComment]);

  // Validação em tempo real
  const validateContent = (value) => {
    const trimmedValue = value.trim();
    
    if (trimmedValue.length === 0) {
      setValidationError("O comentário não pode estar vazio.");
      return false;
    }
    
    if (trimmedValue.length > maxLength) {
      setValidationError(`O comentário não pode ter mais de ${maxLength} caracteres.`);
      return false;
    }
    
    setValidationError("");
    return true;
  };

  // Buscar lista de usuários (cache local)
  const fetchUsers = useCallback(async () => {
    if (allUsers) return allUsers;
    try {
      setMentionLoading(true);
      const res = await getUsers();
      const users = res.data || [];
      setAllUsers(users);
      return users;
    } catch (err) {
      console.error("Erro ao buscar usuários:", err);
      return [];
    } finally {
      setMentionLoading(false);
    }
  }, [allUsers]);

  // Detecta @query no texto baseado na posição do cursor
  const detectMention = useCallback((text, cursorPos) => {
    const textBeforeCursor = text.substring(0, cursorPos);
    // Procura @ que não seja precedido por [ (para não conflitar com tags)
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      return mentionMatch[1]; // query sem o @
    }
    return null;
  }, []);

  // Filtra usuários pelo query
  const filterUsers = useCallback((users, query) => {
    if (!query) return users.slice(0, 8);
    const q = query.toLowerCase();
    return users
      .filter(u => (u.userName || u.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, []);

  // Seleciona menção: remove @query do texto e adiciona chip
  const handleMentionSelect = useCallback((user) => {
    const input = textFieldRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart || content.length;
    const textBeforeCursor = content.substring(0, cursorPos);
    const textAfterCursor = content.substring(cursorPos);
    
    const atIndex = textBeforeCursor.lastIndexOf("@");
    if (atIndex === -1) return;

    // Remove @query do texto
    const newContent = textBeforeCursor.substring(0, atIndex) + textAfterCursor;
    setContent(newContent);
    setCharCount(newContent.length);

    // Adiciona chip
    const userName = user.userName || user.name;
    setPendingTags(prev => [...prev, { type: 'mencao', label: userName, id: user.id }]);

    setShowMentionDropdown(false);
    setMentionQuery("");
    setMentionIndex(0);

    setTimeout(() => {
      if (input) {
        input.focus();
        input.setSelectionRange(atIndex, atIndex);
      }
    }, 0);
  }, [content]);

  const handleContentChange = (event) => {
    const value = event.target.value;
    const cursorPos = event.target.selectionStart || value.length;
    setContent(value);
    setCharCount(value.length);
    
    // Validação em tempo real
    if (value.trim().length > 0) {
      validateContent(value);
    } else {
      setValidationError("");
    }

    // Detecção de menção
    const query = detectMention(value, cursorPos);
    if (query !== null) {
      setMentionQuery(query);
      setShowMentionDropdown(true);
      setMentionIndex(0);

      // Debounce para buscar/filtrar usuários
      if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current);
      mentionDebounceRef.current = setTimeout(async () => {
        const users = await fetchUsers();
        setMentionUsers(filterUsers(users, query));
      }, 200);
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFile(true);
    setValidationError("");

    try {
      for (const file of files) {
        const response = await uploadFileToOneDrive(file, projectNumber);
        const driveId = response.data.id;
        setPendingTags(prev => [...prev, { type: 'arquivo', label: file.name, id: driveId }]);
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      setValidationError('Erro ao anexar arquivo. Tente novamente.');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveTag = (index) => {
    setPendingTags(prev => prev.filter((_, i) => i !== index));
  };

  // Handlers para Drag & Drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Só remove o estado se realmente saiu do dropZone
    const rect = dropZoneRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX;
      const y = e.clientY;
      if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
        setIsDragging(false);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Simula o evento de file input
      const fakeEvent = { target: { files } };
      await handleFileSelect(fakeEvent);
    }
  };

  // Handler para arquivo selecionado do OneDrive
  const handleSelectExistingFile = (file) => {
    setPendingTags(prev => [...prev, { type: 'arquivo', label: file.name, id: file.id }]);
    setShowFileModal(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Monta conteúdo final com tags serializadas
    const tagStrings = pendingTags.map(t => `[${t.type}:${t.label}|${t.id}]`);
    const finalContent = [content.trim(), ...tagStrings].filter(Boolean).join(' ');

    if (!finalContent.trim()) {
      setValidationError("O comentário não pode estar vazio.");
      return;
    }

    try {
      if (isEditing) {
        await updateComment({
          commentId: editingComment.commentId,
          data: { content: finalContent },
        });
        if (onCancelEdit) onCancelEdit();
      } else {
        await createComment({
          content: finalContent,
          projectId: projectId,
        });
      }
      
      // Limpa formulário após sucesso
      setContent("");
      setCharCount(0);
      setValidationError("");
      setPendingTags([]);
      
      // Notifica componente pai
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (error) {
      console.error("Erro ao salvar comentário:", error);
    }
  };

  const handleCancelEdit = () => {
    setContent("");
    setCharCount(0);
    setPendingTags([]);
    setValidationError("");
    if (onCancelEdit) onCancelEdit();
  };

  // Removidas variáveis de estilo não utilizadas após modernização

  return (
    <>
      <Box sx={{ 
        backgroundColor: theme.palette.background.paper,
        px: 1,
        py: 1,
        ...(isEditing && {
          border: `1px solid ${theme.palette.warning.main}40`,
          borderRadius: "8px",
          bgcolor: theme.palette.warning.main + "08",
        }),
      }}>
        {isEditing && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, px: 0.5 }}>
            <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontWeight: 600, fontSize: "0.7rem" }}>
              Editando comentário
            </Typography>
            <IconButton size="small" onClick={handleCancelEdit} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            </IconButton>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Box 
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{ 
              position: "relative",
              border: isDragging ? `2px dashed ${theme.palette.primary.main}` : 'none',
              backgroundColor: isDragging ? theme.palette.primary.light + '10' : 'transparent',
              borderRadius: '8px',
              transition: 'all 0.2s',
            }}
          >
            {isDragging && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.primary.light + '20',
                  borderRadius: '8px',
                  zIndex: 10,
                  pointerEvents: 'none',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                >
                  Solte o arquivo aqui
                </Typography>
              </Box>
            )}
          <TextField
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            placeholder="Escreva um comentário... Use @ para mencionar"
            value={content}
            onChange={handleContentChange}
            inputRef={textFieldRef}
            onKeyDown={(e) => {
              // Navegação do dropdown de menção
              if (showMentionDropdown && mentionUsers.length > 0) {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setMentionIndex(prev => Math.min(prev + 1, mentionUsers.length - 1));
                  return;
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setMentionIndex(prev => Math.max(prev - 1, 0));
                  return;
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleMentionSelect(mentionUsers[mentionIndex]);
                  return;
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setShowMentionDropdown(false);
                  return;
                }
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            disabled={createCommentLoading}
            error={!!validationError}
            variant="standard"
            sx={{
              "& .MuiInput-root": {
                fontSize: "0.875rem",
                "&:before": { display: "none" },
                "&:after": { display: "none" },
              },
              "& .MuiInput-input": {
                padding: "8px 4px",
                fontSize: "0.875rem",
                "&::placeholder": {
                  color: theme.palette.text.secondary,
                  opacity: 0.7,
                },
              },
            }}
            inputProps={{
              maxLength: maxLength + 50,
              'aria-label': 'Campo de texto para novo comentário',
              'aria-describedby': 'comment-char-count',
            }}
          />

          {/* Dropdown de menção */}
          {showMentionDropdown && (
            <Paper
              elevation={4}
              sx={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                right: 0,
                mb: 0.5,
                maxHeight: 240,
                overflow: "auto",
                zIndex: 20,
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              {mentionLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : mentionUsers.length > 0 ? (
                <List dense disablePadding>
                  {mentionUsers.map((user, idx) => (
                    <ListItemButton
                      key={user.id}
                      selected={idx === mentionIndex}
                      onClick={() => handleMentionSelect(user)}
                      sx={{
                        py: 0.75,
                        px: 1.5,
                        "&.Mui-selected": {
                          backgroundColor: theme.palette.primary.light + "30",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1,
                          fontSize: "0.75rem",
                          bgcolor: theme.palette.primary.main,
                        }}
                      >
                        {(user.userName || user.name || "?")[0].toUpperCase()}
                      </Avatar>
                      <ListItemText
                        primary={user.userName || user.name}
                        primaryTypographyProps={{ fontSize: "0.85rem" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ py: 1.5, px: 2, color: theme.palette.text.secondary }}
                >
                  Nenhum usuário encontrado
                </Typography>
              )}
            </Paper>
          )}
          
          {/* Chips de tags pendentes (menções e arquivos) */}
          {pendingTags.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.5 }}>
              {pendingTags.map((tag, idx) => (
                <Chip
                  key={`${tag.type}-${tag.id}-${idx}`}
                  icon={
                    tag.type === 'arquivo'
                      ? <AttachFileIcon fontSize="small" />
                      : <span style={{ fontSize: 13, marginLeft: 8, fontWeight: 700 }}>@</span>
                  }
                  label={tag.label}
                  size="small"
                  color={tag.type === 'mencao' ? 'primary' : 'default'}
                  variant="outlined"
                  onDelete={() => handleRemoveTag(idx)}
                  sx={{
                    height: 26,
                    fontSize: '0.8rem',
                    cursor: 'default',
                  }}
                />
              ))}
            </Stack>
          )}
          
          {/* Botão de anexo, contador e enviar */}
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              mt: 1,
              px: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="comment-file-input"
              />
              <Tooltip title="Anexar arquivo">
                <span>
                  <IconButton
                    component="label"
                    htmlFor="comment-file-input"
                    disabled={uploadingFile || createCommentLoading}
                    size="small"
                    sx={{
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.light + '20',
                      },
                    }}
                  >
                    {uploadingFile ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AttachFileIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              
              <Tooltip title="Referenciar arquivo do OneDrive">
                <span>
                  <IconButton
                    onClick={() => setShowFileModal(true)}
                    disabled={uploadingFile || createCommentLoading}
                    size="small"
                    sx={{
                      color: theme.palette.secondary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.light + '20',
                      },
                    }}
                  >
                    <FolderIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              
              <Typography 
                id="comment-char-count"
                sx={{
                  fontSize: "0.75rem",
                  color: charCount > maxLength * 0.9 
                    ? theme.palette.warning.main 
                    : theme.palette.text.secondary,
                }}
                aria-live="polite"
              >
                {content.trim() ? `${charCount}/${maxLength}` : "Enter para enviar • Shift+Enter pula linha"}
              </Typography>
            </Box>

            <CustomButton
              handleButtonClick={handleSubmit}
              buttonText={isEditing ? "Salvar" : "Enviar"}
              disabled={
                createCommentLoading || updateCommentLoading ||
                (!content.trim() && pendingTags.length === 0) || 
                !!validationError ||
                charCount > maxLength
              }
              isPrimary={true}
              sx={{
                minWidth: 84,
                height: 32,
                fontSize: "0.75rem",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
              }}
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
            />
          </Box>
          </Box>

        {/* Erro de API - versão moderna */}
        {createCommentError && (
          <Fade in={true}>
            <Alert 
              severity="error" 
              variant="outlined"
              sx={{ 
                mt: 1, 
                py: 0.5,
                fontSize: "0.8rem",
                borderRadius: "8px",
              }}
            >
              {createCommentError?.response?.data?.message || 
               "Erro ao adicionar comentário"}
            </Alert>
          </Fade>
        )}

        {/* Validação inline */}
        {validationError && (
          <Typography 
            sx={{
              fontSize: "0.75rem",
              color: theme.palette.error.main,
              mt: 0.5,
              px: 1,
            }}
          >
            {validationError}
          </Typography>
        )}
      </form>
    </Box>

    {/* Modal com FileManager para selecionar arquivo do OneDrive */}
    <Dialog 
      open={showFileModal} 
      onClose={() => setShowFileModal(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Selecionar Arquivo do OneDrive
        <IconButton
          onClick={() => setShowFileModal(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FileManager 
          rootDirectory={projectNumber}
          noAccordion={true}
          selectionMode={true}
          onFileSelect={handleSelectExistingFile}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowFileModal(false)}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default CommentForm;